import { BaseButton, type BaseButtonProps } from '@/src/components/buttons/BaseButton'
import {
  Menu as ChakraMenu,
  MenuButton as ChakraMenuButton,
  type MenuButtonProps as ChakraMenuButtonProps,
  MenuGroup as ChakraMenuGroup,
  type MenuGroupProps as ChakraMenuGroupProps,
  MenuItem as ChakraMenuItem,
  MenuList as ChakraMenuList,
  type MenuListProps as ChakraMenuListProps,
  type MenuProps as ChakraMenuProps,
  Flex,
  MenuItemOption,
  type MenuItemOptionProps,
  forwardRef,
} from '@chakra-ui/react'
import type { ReactElement, ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'
import { RxCaretRight } from 'react-icons/rx'
import { BaseText } from '../BaseText'
import { LocationBadge } from '../LocationBadge'
import { TruncatedTextWithTooltip } from '../TruncatedTextWithTooltip'

// Root Menu Component
interface MenuProps extends ChakraMenuProps {}

export const Menu = ({ children, ...props }: MenuProps) => {
  return <ChakraMenu {...props}>{children}</ChakraMenu>
}

// Menu Button Component
interface MenuButtonProps extends ChakraMenuButtonProps, Omit<BaseButtonProps, keyof ChakraMenuButtonProps> {}

export const MenuButton = forwardRef<MenuButtonProps, 'button'>((props, ref) => {
  const { variant = 'secondary', ...restProps } = props
  return <ChakraMenuButton ref={ref} as={BaseButton} variant={variant} {...restProps} />
})
MenuButton.displayName = 'MenuButton'

// Menu Group Component
interface MenuGroupProps extends ChakraMenuGroupProps {}

export const MenuGroup = forwardRef<MenuGroupProps, 'div'>((props, ref) => {
  return <ChakraMenuGroup ref={ref} {...props} />
})
MenuGroup.displayName = 'MenuGroup'

// Menu List Component
interface MenuListProps extends ChakraMenuListProps {}

export const MenuList = forwardRef<MenuListProps, 'div'>((props, ref) => {
  return <ChakraMenuList ref={ref} padding='4px' {...props} />
})
MenuList.displayName = 'MenuList'

interface MenuSubMenuProps {
  label: string
  children: ReactNode
  isDisabled?: boolean
  width?: string
  // Width of the options list only; defaults to `width` so the trigger row stays uniform when widened.
  listWidth?: string
}

// Submenu component
export const MenuSubMenu = ({ label, children, isDisabled, width = '180px', listWidth }: MenuSubMenuProps) => {
  const [isOpen, setIsOpen] = useState(false)

  const closeTimeoutRef = useRef<number | undefined>(undefined)

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      window.clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = undefined
    }
  }

  const open = () => {
    if (isDisabled) return
    clearCloseTimeout()
    setIsOpen(true)
  }

  // Small delay prevents flicker when moving mouse from trigger -> submenu list.
  const scheduleClose = () => {
    clearCloseTimeout()
    closeTimeoutRef.current = window.setTimeout(() => setIsOpen(false), 120)
  }

  useEffect(() => {
    return () => clearCloseTimeout()
  }, [])

  return (
    <ChakraMenu isOpen={isOpen} placement='right-start' gutter={4} isLazy autoSelect={false}>
      <ChakraMenuButton
        as={ChakraMenuItem}
        icon={undefined}
        color={'foreground'}
        bg={'background'}
        _hover={{ bg: 'muted' }}
        borderRadius='8px'
        isDisabled={isDisabled}
        w={width}
        onMouseEnter={open}
        onMouseLeave={scheduleClose}
      >
        <Flex alignItems='center' justifyContent='space-between' width='full' gap='8px'>
          <TruncatedTextWithTooltip label={label} textStyle='sm/text-sm Regular' flex='1' color={isDisabled ? 'gray.400' : 'inherit'} />
          <Flex alignItems='center' flexShrink={0}>
            <RxCaretRight size={22} />
          </Flex>
        </Flex>
      </ChakraMenuButton>

      <ChakraMenuList padding='4px' minW='0' w={listWidth ?? width} onMouseEnter={open} onMouseLeave={scheduleClose}>
        {children}
      </ChakraMenuList>
    </ChakraMenu>
  )
}

type NonOptionMenuItemCommonProps = Omit<MenuItemOptionProps, 'type'> & {
  icon?: ReactElement | null
  isSelected?: boolean
}

interface StandardMenuItemProps extends NonOptionMenuItemCommonProps {
  class: 'standard'
}

interface DangerMenuItemProps extends NonOptionMenuItemCommonProps {
  class: 'danger'
}

interface ChevronMenuItemProps extends NonOptionMenuItemCommonProps {
  class: 'chevron'
}

interface CheckboxMenuItemProps extends MenuItemOptionProps {
  class: 'checkbox'
  icon?: ReactElement | null
  isSelected?: boolean
}

interface RadioMenuItemProps extends MenuItemOptionProps {
  class: 'radio'
  icon?: ReactElement | null
  isSelected?: boolean
}

interface CreateNewMenuItemProps extends NonOptionMenuItemCommonProps {
  class: 'create-new'
}

interface SearchMenuItemProps extends NonOptionMenuItemCommonProps {
  class: 'search'
}

interface LabelMenuItemProps extends NonOptionMenuItemCommonProps {
  class: 'label'
}

interface DividerMenuItemProps extends NonOptionMenuItemCommonProps {
  class: 'divider'
}

interface FootnoteMenuItemProps extends NonOptionMenuItemCommonProps {
  class: 'footnote'
}

interface LocationMenuItemProps extends NonOptionMenuItemCommonProps {
  class: 'location'
  locationId?: number
  buildingId?: number
  locationFirebaseId?: string
}

type MenuItemProps =
  | StandardMenuItemProps
  | DangerMenuItemProps
  | ChevronMenuItemProps
  | CheckboxMenuItemProps
  | RadioMenuItemProps
  | LabelMenuItemProps
  | DividerMenuItemProps
  | CreateNewMenuItemProps
  | SearchMenuItemProps
  | FootnoteMenuItemProps
  | LocationMenuItemProps

export const MenuItem = forwardRef<MenuItemProps, 'button'>((props, ref) => {
  const { class: variant, isSelected, icon, children, ...restProps } = props
  const chakraIcon: ReactElement | undefined = icon ?? undefined

  const commonProps = {
    ref,
    color: isSelected ? 'primaryForeground' : 'foreground',
    bg: isSelected ? 'accentPrimary' : 'background',
    _hover: { bg: isSelected ? 'accentPrimary' : 'muted' },
    borderRadius: '8px',
  }

  if (variant === 'danger') {
    commonProps.color = '#EF4444'
    commonProps._hover = { bg: 'red.50' }
  }

  if (variant === 'location') {
    const { locationFirebaseId, buildingId, locationId, ...menuItemProps } = restProps as Omit<
      LocationMenuItemProps,
      'class' | 'isSelected' | 'icon'
    >
    return (
      <MenuItemOption {...commonProps} icon={icon ?? null} {...menuItemProps}>
        <Flex data-name='flex' alignItems='center' justifyContent='space-between' width='full' gap={'60px'}>
          <BaseText textStyle='sm/text-sm Regular'>{children}</BaseText>
          <LocationBadge locationFirebaseId={locationFirebaseId} buildingId={buildingId} locationId={locationId} />
        </Flex>
      </MenuItemOption>
    )
  }

  if (variant === 'checkbox' || variant === 'radio') {
    return (
      <MenuItemOption {...commonProps} icon={icon ?? null} {...(restProps as Omit<MenuItemOptionProps, 'icon'>)}>
        <BaseText textStyle='sm/text-sm Regular' asBox={true} display='flex' alignItems='center' gap='6px'>
          {children}
        </BaseText>
      </MenuItemOption>
    )
  }

  return (
    <ChakraMenuItem
      {...commonProps}
      {...(restProps as Omit<NonOptionMenuItemCommonProps, 'icon' | 'isSelected'>)}
      icon={chakraIcon}
      iconSpacing={chakraIcon ? undefined : 0}
    >
      <BaseText
        textStyle='sm/text-sm Regular'
        asBox={true}
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        gap='6px'
        width='100%'
        color='inherit'
      >
        {children}
      </BaseText>
    </ChakraMenuItem>
  )
})
MenuItem.displayName = 'MenuItem'
